import React from 'react';
import SiteLogo from '../Common/SiteLogo';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Avatar,
  Tooltip,
  Box,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { Link } from 'react-router-dom';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import { appBarSx } from '../Common/formStyles';
import { BRAND } from '../../theme/tokens';

const DashboardAppBar = ({ user, currentResumeTitle, onNewResume, isAccountPage = false }) => {
  const theme = useTheme();
  const { mode, toggleTheme } = useAppTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const displayName = user?.name || user?.email?.split('@')[0] || 'Account';

  return (
    <AppBar
      position="static"
      elevation={0}
      color="inherit"
      sx={[appBarSx, { position: 'relative' }]}
    >
      <Toolbar sx={{ gap: 0.5, minHeight: { xs: 56, sm: 64 }, px: { xs: 1.5, sm: 2.5 } }}>
        <SiteLogo
          to="/"
          size={isMobile ? 28 : 32}
          gradientText
          textVariant="h6"
          showText={!isMobile}
          sx={{ flexShrink: 0, minWidth: 0 }}
          textSx={{ fontSize: { sm: '1.1rem' }, whiteSpace: 'nowrap' }}
        />

        {currentResumeTitle && (
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'block',
              px: { xs: 0.75, sm: 2 },
              fontWeight: 500,
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
            }}
          >
            {currentResumeTitle}
          </Typography>
        )}

        <Box sx={{ flexGrow: currentResumeTitle ? 0 : 1, flexShrink: 0 }} />

        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={onNewResume}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            display: { xs: 'none', sm: 'inline-flex' },
            flexShrink: 0,
            background: BRAND.gradient,
            boxShadow: '0 4px 14px rgba(99,102,241,0.3)',
            '&:hover': {
              background: BRAND.gradientHover,
              boxShadow: '0 6px 18px rgba(99,102,241,0.38)',
            },
          }}
        >
          New Resume
        </Button>
        <Tooltip title="New Resume">
          <IconButton
            onClick={onNewResume}
            sx={{
              display: { xs: 'inline-flex', sm: 'none' },
              flexShrink: 0,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {!isAccountPage && (
          <>
            <Button
              component={Link}
              to="/account"
              size="small"
              startIcon={<FolderOpenIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: 'text.primary',
                display: { xs: 'none', md: 'inline-flex' },
                flexShrink: 0,
              }}
            >
              My Resumes
            </Button>
            <Tooltip title="My Resumes">
              <IconButton
                component={Link}
                to="/account"
                sx={{ display: { xs: 'inline-flex', md: 'none' }, flexShrink: 0 }}
              >
                <FolderOpenIcon />
              </IconButton>
            </Tooltip>
          </>
        )}

        <Tooltip title={mode === 'dark' ? 'Light mode' : 'Dark mode'}>
          <IconButton onClick={toggleTheme} size="small" sx={{ flexShrink: 0 }}>
            {mode === 'dark' ? (
              <LightModeOutlinedIcon fontSize="small" />
            ) : (
              <DarkModeOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title={displayName}>
          <IconButton
            component={isAccountPage ? 'div' : Link}
            {...(isAccountPage ? {} : { to: '/account' })}
            sx={{ p: 0.5, ml: 0.25, flexShrink: 0 }}
          >
            {user?.picture ? (
              <Avatar src={user.picture} sx={{ width: 32, height: 32 }} />
            ) : (
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  background: BRAND.gradient,
                  fontSize: '0.85rem',
                  fontWeight: 700,
                }}
              >
                {user?.email?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
            )}
          </IconButton>
        </Tooltip>
      </Toolbar>
    </AppBar>
  );
};

export default DashboardAppBar;
